import { useRef, useState } from 'react';
import { ReactComponent as CounterScreen } from '../assets/landing/counter.svg';
import { ReactComponent as StockScreen } from '../assets/landing/stock.svg';
import { ReactComponent as SalesScreen } from '../assets/landing/sales.svg';
import { ReactComponent as TransactionsScreen } from '../assets/landing/transactions.svg';
import { LANDING_SECTIONS, LANDING_SHOWCASE } from '../constants/posLandingConst';

/** The drawn screen for each tab, keyed like LANDING_SHOWCASE. */
const SCREENS = {
  counter: CounterScreen,
  stock: StockScreen,
  sales: SalesScreen,
  transactions: TransactionsScreen,
};

/**
 * "See it work": four tabs, each a short explanation beside an illustration
 * of that screen. Arrow keys move between tabs, as a tab list should.
 *
 * @returns {JSX.Element}
 */
function PosLandingShowcase() {
  const [active, setActive] = useState(0);
  const tabRefs = useRef([]);

  /**
   * Left/right arrow moves to the neighbouring tab, wrapping at the ends.
   *
   * @param {React.KeyboardEvent} event
   * @param {number} index
   * @returns {void}
   */
  const handleKeyDown = (event, index) => {
    const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!delta) return;
    event.preventDefault();
    const next = (index + delta + LANDING_SHOWCASE.length) % LANDING_SHOWCASE.length;
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <section className="show" id={LANDING_SECTIONS.work}>
      <div className="wrap">
        <div className="head">
          <h2>See it work</h2>
          <p>Four screens cover the whole day, from the first coffee to closing the books.</p>
        </div>

        <div className="tabs" role="tablist" aria-label="Product screens">
          {LANDING_SHOWCASE.map((item, index) => (
            <button
              key={item.key}
              ref={(element) => { tabRefs.current[index] = element; }}
              type="button"
              className="tab"
              role="tab"
              id={`kl-tab-${item.key}`}
              aria-selected={active === index}
              aria-controls={`kl-panel-${item.key}`}
              onClick={() => setActive(index)}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              {item.tab}
            </button>
          ))}
        </div>

        {LANDING_SHOWCASE.map((item, index) => {
          const Screen = SCREENS[item.key];
          return (
            <div
              key={item.key}
              className="panel"
              id={`kl-panel-${item.key}`}
              role="tabpanel"
              aria-labelledby={`kl-tab-${item.key}`}
              data-on={active === index ? '' : undefined}
            >
              <div className="panel-copy">
                <h3>{item.title}</h3>
                {item.body.map((line) => <p key={line}>{line}</p>)}
              </div>
              <figure
                className="screen shot"
                style={{ animationDuration: `${item.floatSeconds}s`, animationDelay: `${item.floatDelay}s` }}
              >
                <div className="shot-in">
                  <div className="shot-bar"><i /><i /><i /></div>
                  <Screen />
                </div>
              </figure>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default PosLandingShowcase;
